/**
 * The analysis summary (DESIGN UD-22 before/after overview): the few figures of one analysis that a rewrite job keeps to show
 * "before" and "after" side by side, so reopening a finished job needs no analysis. It holds the machine-authorship index
 * (the calibrated index the overview headline shows, or the seed-rules composite when the model is uncalibrated), its status
 * and whether it is flagged at the reference point, the translation index when the document has one, and the document score
 * of every channel (all of them: a channel that does not apply keeps its reason). Plain data, structured-cloneable, a few
 * kilobytes, so it goes into the job record in IndexedDB.
 *
 * `summariseAnalysis` reads an ensemble result, so the same function summarises the analysed document (from its view model's
 * `ensemble`) and the rewritten text (from the worker's `summarise` request, `src/worker/summary.ts`): both come out of the
 * same engine. The wording of the statuses is the engine's (`STATUS_PHRASE`); nothing here compares a value to the reference
 * point or calls a figure a probability. Types and pure functions only, so it is tested in Node.
 */
import type { ChannelId } from '../engine/channels/types.ts';
import type { EnsembleResult } from '../engine/ensemble/index.ts';

export const SUMMARY_VERSION = 1;

/** What an index figure is: the calibrated index, the uncalibrated composite or score, or nothing to show. */
export type IndexKind = 'calibrated' | 'uncalibrated' | 'none';

export interface IndexSummary {
	kind: IndexKind;
	/** The figure on the 0 to 100 index scale, or null (no value). */
	value: number | null;
	/** The engine's wording of the status ("flagged at the reference point"); empty for an uncalibrated figure. */
	statusPhrase: string;
	/** Above the reference point: the status is "flagged". Only a calibrated index can be flagged. */
	flagged: boolean;
	/** The reference point on the index scale, for a calibrated index. */
	referenceIndex: number | null;
}

export interface ChannelSummary {
	id: ChannelId;
	name: string;
	axis: 'authorship' | 'translation';
	/** The channel's document score (0 to 100), or null when it does not apply to the text. */
	score: number | null;
	/** Why the channel abstained, when it did. */
	reason?: string;
}

export interface AnalysisSummary {
	version: typeof SUMMARY_VERSION;
	words: number;
	/** ISO 8601: when the analysis was made. */
	at: string;
	/** The authorship model's id and whether it is calibrated (the caveat's wording depends on it). */
	modelId: string;
	calibrated: boolean;
	authorship: IndexSummary;
	translation: IndexSummary;
	channels: ChannelSummary[];
}

/** The parts of an ensemble result the summary reads (the view model's slim ensemble has them all). */
export type SummarySource = Pick<
	EnsembleResult,
	'index' | 'model' | 'calibratedIndex' | 'translation' | 'channels'
>;

const NONE: IndexSummary = {
	kind: 'none',
	value: null,
	statusPhrase: '',
	flagged: false,
	referenceIndex: null,
};

/** The authorship axis: the calibrated index when the model has one, else the seed-rules composite. */
function authorshipOf(e: SummarySource): IndexSummary {
	const ci = e.calibratedIndex;
	if (ci)
		return {
			kind: 'calibrated',
			value: ci.index,
			statusPhrase: ci.statusPhrase,
			flagged: ci.status === 'flagged',
			referenceIndex: ci.referenceIndex,
		};
	return {
		kind: 'uncalibrated',
		value: e.index,
		statusPhrase: '',
		flagged: false,
		referenceIndex: null,
	};
}

/**
 * The translation axis. The calibrated translation index is the figure only when the gate G4 passed (the overview's own rule,
 * T2-DESIGN §8); otherwise the MT-01 score stands in, uncalibrated; with neither there is nothing to show.
 */
function translationOf(e: SummarySource): IndexSummary {
	const t = e.translation;
	const tci = t.g4 === true ? t.calibratedIndex : undefined;
	if (tci)
		return {
			kind: 'calibrated',
			value: tci.index,
			statusPhrase: tci.statusPhrase,
			flagged: tci.status === 'flagged',
			referenceIndex: tci.referenceIndex,
		};
	if (t.score !== null && t.score !== undefined)
		return {
			kind: 'uncalibrated',
			value: t.score,
			statusPhrase: '',
			flagged: false,
			referenceIndex: null,
		};
	return NONE;
}

/** Summarises one analysis. `words` is the intake word count; `at` is when the analysis was made. */
export function summariseAnalysis(
	e: SummarySource,
	meta: { words: number; at: string }
): AnalysisSummary {
	return {
		version: SUMMARY_VERSION,
		words: meta.words,
		at: meta.at,
		modelId: e.model.id,
		calibrated: e.model.calibrated,
		authorship: authorshipOf(e),
		translation: translationOf(e),
		channels: e.channels.map((c) => ({
			id: c.id,
			name: c.name,
			axis: c.axis,
			score: c.status === 'scored' && c.score !== undefined ? c.score : null,
			...(c.status === 'abstain' && c.reason ? { reason: c.reason } : {}),
		})),
	};
}

/* ---------------- the comparison ---------------- */

/** One channel across the two analyses. */
export interface ChannelCompare {
	id: ChannelId;
	name: string;
	axis: 'authorship' | 'translation';
	before: number | null;
	after: number | null;
	/** After minus before; null when either side does not apply. */
	change: number | null;
	/** The channel rose above its baseline plus its floor after the run (the lens's "above its baseline"). */
	over: boolean;
	/** The reason it does not apply, when it does not (before first, else after). */
	reason?: string;
}

/**
 * Every channel of either analysis, once: the before and after score, the change and the mark. A channel missing from one
 * summary (a record from another engine version) has a null on that side. Channels keep the engine's order.
 */
export function compareChannels(
	before: AnalysisSummary,
	after: AnalysisSummary,
	over: Iterable<ChannelId> = []
): ChannelCompare[] {
	const b = new Map(before.channels.map((c) => [c.id, c]));
	const a = new Map(after.channels.map((c) => [c.id, c]));
	const mark = new Set(over);
	const ids: ChannelId[] = [...new Set<ChannelId>([...b.keys(), ...a.keys()])];
	return ids.map((id) => {
		const cb = b.get(id);
		const ca = a.get(id);
		const sb = cb?.score ?? null;
		const sa = ca?.score ?? null;
		const reason = cb?.reason ?? ca?.reason;
		return {
			id,
			name: (cb ?? ca)?.name ?? id,
			axis: (cb ?? ca)?.axis ?? 'authorship',
			before: sb,
			after: sa,
			change: sb !== null && sa !== null ? sa - sb : null,
			over: mark.has(id),
			...(reason ? { reason } : {}),
		};
	});
}

/** An index figure's change, or null when either side has no value. */
export function indexChange(before: IndexSummary, after: IndexSummary): number | null {
	return before.value !== null && after.value !== null ? after.value - before.value : null;
}

export type ChannelSort = 'change' | 'id' | 'before' | 'after';

/**
 * Orders the rows: `change` puts the largest move (either way) first, `before` and `after` the highest score first, `id` the
 * channel id. A channel that does not apply sorts last in every numeric order; ties fall back to the id. `dir` reverses.
 */
export function sortCompare(
	rows: readonly ChannelCompare[],
	key: ChannelSort,
	dir: 'asc' | 'desc' = key === 'id' ? 'asc' : 'desc'
): ChannelCompare[] {
	const val = (r: ChannelCompare): number | null =>
		key === 'change'
			? r.change === null
				? null
				: Math.abs(r.change)
			: key === 'id'
				? null
				: r[key];
	const sign = dir === 'asc' ? 1 : -1;
	return [...rows].sort((x, y) => {
		if (key === 'id') return sign * (x.id < y.id ? -1 : x.id > y.id ? 1 : 0);
		const vx = val(x);
		const vy = val(y);
		if (vx === null && vy === null) return x.id < y.id ? -1 : 1;
		if (vx === null) return 1;
		if (vy === null) return -1;
		return sign * (vx - vy) || (x.id < y.id ? -1 : 1);
	});
}
